import { IPD_FREQUENCY_CODES, type IpdFrequencyCode } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { Field, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { InstantPicker } from './controls';
import type { CommonDraft, ScheduleTiming } from './order-form';

export interface ScheduleDraft {
  timing: ScheduleTiming;
  frequency: IpdFrequencyCode | '';
  intervalHours: string;
  intervalMins: string;
}

const FREQUENCY_OPTIONS = () => IPD_FREQUENCY_CODES.map((code) => ({ value: code, label: tr('ipd', `io_form_freq_${code}`) }));

export function FrequencyOrInterval({
  draft,
  update,
  error,
  required,
}: {
  draft: ScheduleDraft;
  update: (patch: Partial<ScheduleDraft>) => void;
  error: (...fields: string[]) => string | undefined;
  required?: boolean;
}) {
  return (
    <>
      <div className="space-y-2 sm:col-span-2">
        <ChoiceButtons
          value={draft.timing}
          label={tr('ipd', 'io_form_how_often')}
          onChange={(timing) => update({ timing })}
          options={[
            { value: 'frequency', label: tr('ipd', 'io_form_by_frequency') },
            { value: 'interval', label: tr('ipd', 'io_form_by_interval') },
          ]}
        />
      </div>
      {draft.timing === 'frequency' ? (
        <Field label={tr('ipd', 'io_form_frequency')} required={required} error={error('frequency', 'intervalMinutes')} className="sm:col-span-2">
          <ChoiceButtons layout="chips" value={draft.frequency} options={FREQUENCY_OPTIONS()} onChange={(frequency) => update({ frequency })} />
        </Field>
      ) : (
        <Field label={tr('ipd', 'io_form_every')} required={required} error={error('intervalMinutes', 'frequency')} className="sm:col-span-2">
          <div className="grid max-w-sm grid-cols-2 gap-2">
            <label className="space-y-1 text-xs text-muted-foreground">
              <span>{tr('ipd', 'io_form_hours')}</span>
              <Input type="number" inputMode="numeric" min={0} max={168} value={draft.intervalHours} onChange={(event) => update({ intervalHours: event.target.value })} />
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              <span>{tr('ipd', 'io_form_minutes')}</span>
              <Input type="number" inputMode="numeric" min={0} max={59} step={5} value={draft.intervalMins} onChange={(event) => update({ intervalMins: event.target.value })} />
            </label>
          </div>
        </Field>
      )}
    </>
  );
}

export function StopAtField({
  common,
  setCommon,
  error,
  label = tr('ipd', 'io_form_stop_at'),
  hint = tr('ipd', 'io_form_stop_at_hint'),
}: {
  common: CommonDraft;
  setCommon: (patch: Partial<CommonDraft>) => void;
  error: (...fields: string[]) => string | undefined;
  label?: string;
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint} hintBelow error={error('endsAt')}>
      <InstantPicker value={common.endsAt} onChange={(endsAt) => setCommon({ endsAt })} min={common.startsAt || undefined} />
    </Field>
  );
}
